import { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, X } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { useCompanyContextStore, isContextSwitchActive } from '@/store/company-context-store';
import { listCompanies } from '@/api/companies-api';
import { cn } from '@/lib/cn';
import type { Company } from '@/api/types';

export function CompanyContextSwitcher() {
  const { permissions, companyId: userCompanyId } = useAuth();
  const {
    companyName: userCompanyName,
    activeCompanyId,
    activeCompanyName,
    setActiveCompany,
    clearActiveCompany,
  } = useCompanyContextStore();

  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isSuperAdmin = permissions.canManageSystem;
  const isContextActive = isContextSwitchActive();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load companies when dropdown opens
  useEffect(() => {
    if (isOpen && companies.length === 0) {
      loadCompanies();
    }
  }, [isOpen]);

  async function loadCompanies() {
    try {
      setIsLoading(true);
      const response = await listCompanies();
      setCompanies(response.companies);
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  }

  function handleSelectCompany(company: Company) {
    if (company.companyId === userCompanyId) {
      clearActiveCompany();
    } else {
      setActiveCompany(company.companyId, company.name);
    }
    setIsOpen(false);
  }

  function handleClearContext() {
    clearActiveCompany();
    setIsOpen(false);
  }

  // Only render for super-admins
  if (!isSuperAdmin) {
    return null;
  }

  const displayName = isContextActive ? activeCompanyName : (userCompanyName ?? 'My Company');

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 rounded border px-3 py-1.5 text-xs transition-colors',
          isContextActive
            ? 'border-amber-500/50 bg-amber-500/10 text-amber-400'
            : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white',
        )}
      >
        <Building2 className="h-3.5 w-3.5" />
        <span className="max-w-[120px] truncate">{displayName}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded border border-white/10 bg-[hsl(0,0%,7%)] shadow-xl">
          <div className="border-b border-white/5 p-2">
            <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">
              // Company_Context
            </p>
          </div>

          {isContextActive && (
            <div className="border-b border-white/5 p-2">
              <button
                onClick={handleClearContext}
                className="flex w-full items-center justify-between rounded bg-amber-500/10 px-3 py-2 text-xs text-amber-400 hover:bg-amber-500/20"
              >
                <span>Return to My Company</span>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto p-2">
            {isLoading ? (
              <p className="px-3 py-2 text-xs text-gray-500">Loading...</p>
            ) : companies.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-500">No companies found</p>
            ) : (
              <div className="space-y-1">
                {companies.map((company) => {
                  const isSelected = isContextActive
                    ? company.companyId === activeCompanyId
                    : company.companyId === userCompanyId;
                  const isUserCompany = company.companyId === userCompanyId;

                  return (
                    <button
                      key={company.companyId}
                      onClick={() => handleSelectCompany(company)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs transition-colors',
                        isSelected
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-gray-400 hover:bg-white/5 hover:text-white',
                      )}
                    >
                      <Building2 className="h-3.5 w-3.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{company.name}</p>
                        <p className="truncate text-[10px] text-gray-600">{company.companyId}</p>
                      </div>
                      {isUserCompany && (
                        <span className="shrink-0 text-[10px] text-gray-600">(You)</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Banner shown when context switch is active
 */
export function CompanyContextBanner() {
  const { companyId: userCompanyId } = useAuth();
  const { activeCompanyId, activeCompanyName, clearActiveCompany } = useCompanyContextStore();

  const isContextActive = activeCompanyId !== null && activeCompanyId !== userCompanyId;

  if (!isContextActive) {
    return null;
  }

  return (
    <div className="flex items-center justify-between border-b border-warning/30 bg-warning/10 px-4 py-2">
      <div className="flex items-center gap-2 text-xs text-soft">
        <Building2 className="h-4 w-4 text-warning" />
        <span>
          Viewing as: <strong className="text-foreground">{activeCompanyName}</strong>
        </span>
      </div>
      <button
        onClick={clearActiveCompany}
        className="flex items-center gap-1 rounded bg-warning/15 px-2 py-1 text-xs font-medium text-warning hover:bg-warning/25"
      >
        <X className="h-3 w-3" />
        Return to My Company
      </button>
    </div>
  );
}
